// pilarCoerencia.js — espelho frontend da heurística backend
// `diagnosticar_pilar_principal_vs_conteudo` (analysis_field_validator.py).
//
// Decisão de produto Sessão 20 cravada no canônico v37:
//   pilar incoerente NÃO bloqueia salvar — emite warning [PILAR_INCOERENTE]
//   no log do backend pra coletar dados em prod por 1-2 semanas.
//
// Esta TD (Sessão 21 — TD-followup-frontend-mostrar-aviso-pilar) complementa
// visualmente: o usuário vê um ⚠️ ao lado do badge "Pilar principal" quando
// o pilar escolhido aponta pra um ramo do 6M sem conteúdo substantivo. Não
// bloqueia salvar — só sinaliza incoerência durante a edição.
//
// Espelho mantido manualmente: testes (pilarCoerencia.test.js) usam os mesmos
// casos do `test_sessao20_pilar_principal_dominante.py` pra garantir paridade.
// Se a heurística backend mudar, atualizar aqui também.

const PILAR_NOME_PARA_CHAVE = {
  'Mão de obra': 'mao_de_obra',
  'Método': 'metodo',
  'Máquina': 'maquina',
  'Material': 'material',
  'Medição': 'medicao',
  'Meio ambiente': 'meio_ambiente',
};

// Frases canônicas que a IA usa como "vazio semântico" — apesar de o ramo
// ter texto, indica explicitamente que não há contribuição daquele pilar.
// Pilar principal NÃO deveria apontar pra esse caso. (1:1 do tupla Python.)
const RAMO_VAZIO_SEMANTICO = [
  'sem influência observável',
  'sem influencia observavel',
  'sem evidência',
  'sem evidencia',
  'sem informação',
  'sem informacao',
  'nenhuma evidência',
  'nenhuma evidencia',
  'não há evidência',
  'nao ha evidencia',
];

/**
 * Heurística: True se o texto do ramo do 6M tem conteúdo capaz de justificar
 * escolha como pilar principal. Espelha `_ramo_tem_conteudo_substantivo`.
 *
 * @param {*} texto valor do ramo (mao_de_obra, metodo, ...) — string|null|undefined
 * @returns {boolean}
 */
export function ramoTemConteudoSubstantivo(texto) {
  if (texto === null || texto === undefined) return false;
  const s = String(texto).trim();
  if (!s) return false;
  const sLower = s.toLowerCase();
  if (['null', 'none', 'n/a', 'na', '-', '--'].includes(sLower)) return false;
  if (RAMO_VAZIO_SEMANTICO.some(frase => sLower.includes(frase))) return false;
  return s.length >= 20;
}

/**
 * Diagnostica se o `pilar_principal` escolhido aponta pra um ramo do
 * `analise_6m` que tem conteúdo substantivo.
 *
 * Espelho de `diagnosticar_pilar_principal_vs_conteudo` no backend.
 *
 * @param {string|null|undefined} pilarPrincipal valor humano-legível
 *   ("Método", "Mão de obra", etc) — case-sensitive (canonical)
 * @param {object} analise6m objeto com keys snake_case mao_de_obra,
 *   metodo, maquina, material, medicao, meio_ambiente
 * @returns {{coerente: boolean, motivo: string|null, ramoChave: string|null}}
 */
export function diagnosticarPilarPrincipal(pilarPrincipal, analise6m) {
  if (!pilarPrincipal || !analise6m || typeof analise6m !== 'object') {
    return { coerente: true, motivo: null, ramoChave: null };
  }
  const ramoChave = PILAR_NOME_PARA_CHAVE[pilarPrincipal];
  if (!ramoChave) {
    // Pilar não-canônico — outra validação cuida. Aqui devolve coerente=true
    // pra não duplicar aviso.
    return { coerente: true, motivo: null, ramoChave: null };
  }
  const textoRamo = analise6m[ramoChave];
  if (ramoTemConteudoSubstantivo(textoRamo)) {
    return { coerente: true, motivo: null, ramoChave };
  }
  const preview = String(textoRamo || '').slice(0, 60).trim() || '(vazio)';
  return {
    coerente: false,
    motivo:
      `pilar_principal='${pilarPrincipal}' aponta para um ramo do 6M ` +
      `sem conteúdo substantivo (analise_6m.${ramoChave}='${preview}'). ` +
      `Considere preencher o ramo antes ou escolher outro pilar.`,
    ramoChave,
  };
}
